import { getRanaApiV1 } from "@nens/rana/api-v1";
import { retrieveTenantAndOrganisation } from "@nens/ranalib";
import { compareUUIDs } from "@nens/shared-utils";
import * as api from "@nens/threedi/openapi-client";

import { getLoginRedirectUrl, getV3Api } from "../apiConfiguration";
import { AppDispatch, AppState } from "../App";
import { actOnApiError } from "../utils/apiUtils";

import { sendMessage } from "./mainGui";
import { getSimulation } from "./simulation";
import { ENDLESS_SIMULATION_DURATION_LIMIT } from "./simulation";

interface OrganisationsByUniqueId {
  [uniqueId: string]: api.Organisation;
}

interface SessionState {
  authenticated: boolean;
  user: api.AuthProfile | null;
  organisationsFetched: boolean;
  organisationIds: string[];
  organisations: OrganisationsByUniqueId;
}

const INITIAL_STATE: SessionState = {
  authenticated: false,
  user: null,
  organisationsFetched: false,
  organisationIds: [],
  organisations: {},
};

// Action types

const LOGIN = "session/LOGIN";
export const RECEIVE_ORGANISATIONS = "session/RECEIVE_ORGANISATIONS";

interface LoginAction {
  type: typeof LOGIN;
  user: api.AuthProfile;
}

export interface ReceiveOrganisationsAction {
  type: typeof RECEIVE_ORGANISATIONS;
  organisations: api.Organisation[];
}

type SessionAction = LoginAction | ReceiveOrganisationsAction;

// Reducer

export default function reducer(state = INITIAL_STATE, action: SessionAction) {
  switch (action.type) {
    case LOGIN:
      return {
        ...state,
        authenticated: true,
        user: action.user,
      };
    case RECEIVE_ORGANISATIONS: {
      const organisationIds: string[] = [];
      const organisations: OrganisationsByUniqueId = {};
      action.organisations.forEach((org) => {
        organisationIds.push(org.uniqueId);
        organisations[org.uniqueId] = org;
      });
      return {
        ...state,
        organisationIds,
        organisations,
        organisationsFetched: true,
      };
    }
    default:
      return state;
  }
}

// Action creators

export const attemptLogin = () => (dispatch: AppDispatch) => {
  const v3Api = getV3Api();
  v3Api
    .authProfileList({})
    .then((user) => {
      dispatch({
        type: LOGIN,
        user: user,
      });
      const v3Api = getV3Api();
      v3Api
        .organisationsList({
          validContractsOnly: true,
          contractScopeIn: ["livesite"],
          limit: 9999,
        })
        .then((organisations) => {
          const { tenant: tenantId } = retrieveTenantAndOrganisation();
          if (tenantId) {
            getRanaApiV1()
              .TenantsApi.tenantRetrieve({ tenantId })
              .then((tenant) => {
                dispatch({
                  type: RECEIVE_ORGANISATIONS,
                  organisations: organisations.results.filter((org) =>
                    tenant.threediOrganisations.some((tenantOrg) =>
                      compareUUIDs(tenantOrg, org.uniqueId)
                    )
                  ),
                });
              });
          }
        })
        .catch((error: api.ResponseError) => {
          const response = error.response;
          if (
            // check for throttling as here https://github.com/nens/threedi-live-site/issues/894
            response.statusText === "Too Many Requests" &&
            response.status === 429
          ) {
            actOnApiError(
              response,
              (msg, seconds, level) => {
                const sendMessageCurry = sendMessage(msg, seconds * 1000, level);
                dispatch(sendMessageCurry);
              },
              () => {
                attemptLogin()(dispatch);
              }
            );
          }
        });
    })
    .catch((error: api.ResponseError) => {
      const response = error.response;
      if (response?.status === 401) {
        // alert("this alert can be placed here to stop the redirect (untill pressed ok) so that network calls can be debugged");
        window.location.href = getLoginRedirectUrl();
      } else if (
        // check for throttling as here https://github.com/nens/threedi-live-site/issues/894
        response?.statusText === "Too Many Requests" &&
        response?.status === 429
      ) {
        actOnApiError(
          response,
          (msg, seconds, level) => {
            const sendMessageCurry = sendMessage(msg, seconds * 1000, level);
            dispatch(sendMessageCurry);
          },
          () => {
            attemptLogin()(dispatch);
          }
        );
      } else {
        console.log(
          "User is not redirected to login, but this might be a mistake. Check file session.ts line 118 t/m 145"
        );
      }
    });
};

// Selectors

export const isAuthenticated = (state: AppState) => state.session.authenticated;
export const areOrgansationsFetched = (state: AppState) => state.session.organisationsFetched;

export const getUser = (state: AppState) => state.session.user;

export const getUserIsSpectator = (state: AppState) => {
  const user = getUser(state);
  const simulation = getSimulation(state);

  // User is spectator if the simulation is owned by another user, or the
  // simulation is not endless (only Rana Live simulations are endless).
  return (
    !simulation ||
    !user ||
    simulation.user !== user.username ||
    !simulation.duration ||
    simulation.duration < ENDLESS_SIMULATION_DURATION_LIMIT
  );
};

export const allOrganisations = (state: AppState) =>
  state.session.organisationIds.map((uniqueId) => state.session.organisations[uniqueId]);
